import { SanctionRecommendationWithDates } from '@/interfaces/infractions';
import { Person } from '@/interfaces/person';
import { InfringementToEdit } from '@/interfaces/search/search.interface';
import { InfractionService } from '@/services/infraction/infraction.service';
import { SearchService } from '@/services/search/search.service';
import { StoreState } from '@/store';
import { modalShow } from '@/store/modal/actions';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { dateEndSettedOrUndefined } from '@/validators/dateEndSettedOrUndefined';
import { dateMustBeAfterOrEqualThanToday } from '@/validators/dateMustBeAfterOrEqualThanToday';
import { dateMustBeBeforeOrEqualThanToday } from '@/validators/dateMustBeBeforeOrEqualThanToday';
import { mustHaveOneRecord } from '@/validators/mustHaveOneRecord';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { PersonService } from '@/services/person/person.service';
import { ofuscate } from '@/utils/obfuscation';
import { loaderAnimationHide, loaderAnimationShow } from '@/store/page-loader/action';
import { Router } from '@angular/router';
import { parseISOString } from '@/utils/parseISOString';

@Component({
  selector: 'app-search-driver-result-infractions',
  templateUrl: './infractions.component.html',
  styleUrls: ['./infractions.component.css'],
})
export class InfractionsComponent implements OnInit {

  @Input() selectedPersonData: Partial<Person> = {};
  @Input() userRutDv: string = '';

  selectedInfringement: number = 0;
  showNewInfraction: boolean = false;
  formType: 'edit' | '' = '';
  severityName: string = '';
  infrigementName: string = '';
  recommendation?: SanctionRecommendationWithDates;

  infringementDetail: Partial<InfringementToEdit> = {};
  gettingInfringementDetail: boolean = false;
  gettinInfringementDetailError: string = 'DEFAULT_VALUE';

  form: FormGroup = new FormGroup({
    person: new FormGroup({
      id: new FormControl(0, [Validators.required, Validators.min(1)]),
      email: new FormControl('', [Validators.required]),
      rutDv: new FormControl('', [Validators.required]),
    }),
    locationAndEquipment: new FormGroup({
      assetId: new FormControl('', [Validators.required, Validators.min(1)]),
      zoneId: new FormControl('', [Validators.required, Validators.min(1)]),
      vehicleTypeId: new FormControl('', [Validators.required, Validators.min(1)]),
      patent: new FormControl('', [Validators.required]),
    }),
    detail: new FormGroup({
      day: new FormControl('', [Validators.required, dateMustBeBeforeOrEqualThanToday()]),
      hour: new FormControl(''), // now is optional
      personResponsibleId: new FormControl(0), // now is optional
      severityTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
      infrigmentTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
      observation: new FormControl(''),
      reporterTypeId: new FormControl(0), // now is optional
      reporterPersonId: new FormControl(0, [Validators.required, Validators.min(0)]),
      evidenceTypes: new FormControl([]),
      // Al activar el formuario, se borra el 0, al desactivarlo, se agrega el cero.
      witnesses: new FormControl([0], [mustHaveOneRecord('Debe seleccionar al menos un testigo.')]),
    }),
    sanctionType: new FormGroup({
      sanctionTypeId: new FormControl('', [Validators.required, Validators.min(1)]),
      suspensionDays: new FormControl('', [Validators.required]),
      sanctionRange: new FormGroup({
        start: new FormControl(null, [dateMustBeAfterOrEqualThanToday(), dateEndSettedOrUndefined()]),
        end: new FormControl(null, [dateEndSettedOrUndefined(), dateMustBeAfterOrEqualThanToday()]),
      }, [dateEndMustBeAfterDateStart('start', 'end')]),
      expirationDate: new FormControl(null, [dateEndSettedOrUndefined()]),
    }),
  });

  get formGroupLocationAndEquipment(): FormGroup {
    const locationAndEquipmentForm = this.form.controls.locationAndEquipment as FormGroup;
    return locationAndEquipmentForm;
  }

  get formGroupDetail(): FormGroup {
    const detailForm = this.form.controls.detail as FormGroup;
    return detailForm;
  }

  get assetId(): number {
    const locationAndEquipmentForm = this.form.controls.locationAndEquipment as FormGroup;
    return locationAndEquipmentForm.controls.assetId.value;
  }

  get formGroupSanctionType(): FormGroup {
    const sanctionType = this.form.controls.sanctionType as FormGroup;
    return sanctionType;
  }
  constructor(
    private store: Store<StoreState>,
    private router: Router,
    private infractionService: InfractionService,
    private personService: PersonService,
    private searchService: SearchService,
  ) {

    const requestRecomendation$ = combineLatest([
      (<FormGroup>this.form.controls.detail).controls.severityTypeId.valueChanges,
      (<FormGroup>this.form.controls.detail).controls.infrigmentTypeId.valueChanges,
    ]);

    let defaultSetted = false;

    requestRecomendation$.subscribe({
      next: ([severityTypeId, infrigmentTypeId]) => {
        if (severityTypeId && infrigmentTypeId) {
          if (+severityTypeId > 0 && +infrigmentTypeId) {
            // This locks the sanction form until the recommendation is fetched.
            this.recommendation = undefined;
            const { rutDv } = this.form.value.person;
            this.infractionService.getInfringementsQuantityByUser(rutDv).subscribe((infractionQuantity) => {
              const { assetId } = this.form.value.locationAndEquipment;
              const infractionQuantityPlus = infractionQuantity + 1;
              this.infractionService.getRecomendation(assetId, +severityTypeId, infractionQuantityPlus).subscribe({
                next: (value) => {
                  const currentInfrigmentTypeId = (<FormGroup>this.form.controls.detail).controls.infrigmentTypeId.value;
                  // if the value change while fetching data, dont save the response
                  if (infrigmentTypeId !== currentInfrigmentTypeId) {
                    return;
                  }


                  if (!defaultSetted) {
                    defaultSetted = true;
                    this.recommendation = {
                      ...value,
                      days: this.infringementDetail.suspensionDaysId || 0,
                      sanctionTypeId: this.infringementDetail.sanctionTypeId as number,
                    };
                    if (this.infringementDetail.dateStart && this.infringementDetail.dateStart > new Date(2000, 0, 1)) {
                      let { dateEnd } = this.infringementDetail;
                      if (dateEnd) {
                        const limitValidDate = new Date(2010, 0, 1);
                        if (dateEnd.getTime() < limitValidDate.getTime()) {
                          dateEnd = undefined;
                        }
                      }
                      this.recommendation = {
                        ...value,
                        days: this.infringementDetail.suspensionDaysId || 0,
                        sanctionTypeId: this.infringementDetail.sanctionTypeId as number,
                        dateStart: this.infringementDetail.dateStart,
                        dateEnd,
                      };
                    }
                    return;
                  }
                  this.recommendation = value;
                },
              });
            });
          }
          else {
            this.recommendation = undefined;
          }
        }
      },
    });

  }

  ngOnInit(): void {
    this.form.controls.person.patchValue({
      id: this.selectedPersonData.id,
      email: this.selectedPersonData.email,
      rutDv: this.selectedPersonData.mdmid,
    });
  }

  onSelectInfringement(id: number) {
    if (!id) {
      this.goToCreate();
      return;
    }
    this.selectedInfringement = id;
    this.gettingInfringementDetail = true;
    this.searchService.getInfringementDetail(id)
      .subscribe({
        next: (res) => {
          this.infringementDetail = res;

          this.formGroupLocationAndEquipment.patchValue({
            assetId: this.infringementDetail.assetId,
          });
          // Without this delays the code gets bugged
          of(1).pipe(delay(300)).subscribe({
            next: () => {
              this.formGroupLocationAndEquipment.patchValue({
                zoneId: this.infringementDetail.zoneId,
                vehicleTypeId: this.infringementDetail.equipmentId,
                patent: this.infringementDetail.equipmentPatent,
              });

              let defaultHours = `${res.dateTimeInfringement?.getHours()}`;
              if (defaultHours.length === 1) defaultHours = `0${defaultHours}`;

              let defaultMinutes = `${res.dateTimeInfringement?.getMinutes()}`;
              if (defaultMinutes.length === 1) defaultMinutes = `0${defaultMinutes}`;

              const date = res.dateTimeInfringement as Date;
              date.setHours(0, 0, 0, 0);

              this.formGroupDetail.patchValue({
                day: date,
                hour: `${defaultHours}:${defaultMinutes}`,
                personResponsibleId: res.responsableUserId,
                severityTypeId: res.severityId,
                reporterTypeId: res.infringementReportId,
                infrigmentTypeId: res.infringementTypeId,
                evidenceTypes: res.infringementEvidence.map(({ evidenceId }) => evidenceId),
                observation: res.observation,
                reporterPersonId: res.infringementReportUserId,
                witnesses: res.infringementWitness.map(({ witnessUserId }) => witnessUserId),
              });

              this.gettingInfringementDetail = false;
            },
          });
        },
        error: () => {
          this.gettingInfringementDetail = false;
          this.gettinInfringementDetailError = 'Error al obtener el detalle de la infracción.';
        },
      });
    this.showNewInfraction = true;
  }

  onCreateInfringement() {
    this.selectedInfringement = 0;
    this.showNewInfraction = true;
  }

  onHideForm() {
    this.selectedInfringement = 0;
    this.showNewInfraction = false;
  }

  onChangeSeverity(name: string) {
    this.severityName = name;
  }

  onChangeInfringement(name: string) {
    this.infrigementName = name;
  }

  onDisable() {
    this.store.dispatch(modalShow({
      title: '¿Estas seguro de eliminar la infracción?',
      text: `
        La infracción será eliminada automáticamente si continúa en Eliminar infracción. Presiona volver para regresar a la pantalla anterior.
      `,
      text3: 'Al eliminar la última infracción, el estado de vencimiento de los equipos podria verse afectado al siguiente día.',
      acceptButtonText: 'Eliminar infracción',
      actionOnAccept: () => {
        this.store.dispatch(loaderAnimationShow());
        const currentOfuscatedData = this.router.url.split('/').reverse()[1];

        this.searchService.disableInfringement(this.selectedInfringement).subscribe({
          next: () => {
            const ofuscatedData = ofuscate({
              title: 'Eliminar infracción',
              subtitle: `La infracción N°: ${this.selectedInfringement} ha sido eliminada correctamente`,
              message: `Una copia de la eliminación fue enviada al
          correo electrónico ${this.form.value.person.email}`,
              actionButtonPath: `/search/infraction/result/${currentOfuscatedData}/infractions`,
              actionButtonText: 'Ir al perfil de conductor',
            });

            this.store.dispatch(loaderAnimationHide());
            this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);
          },
          error: () => {
            this.store.dispatch(loaderAnimationHide());


            this.store.select('pageError').subscribe({
              next: (pageConfig) => {
                const [website, ...url] = window.location.href.split('//')[1].split('/');
                if (pageConfig.errorType !== 'Mulesoft') {


                  const ofuscatedData = ofuscate({
                    variant: 'failure',
                    title: 'Eliminar infracción',
                    subtitle: 'Hubo un problema y no hemos podido eliminar la infracción. Por favor intenta de nuevo.',
                    actionButtonPath: `/search/infraction/result/${currentOfuscatedData}/infractions`,
                    actionButtonText: 'Ir al perfil de conductor',
                  });
                  this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);

                }
              },
            });

          },
        });
      },
    }));
  }

  onResetForm() {
    this.form.controls.locationAndEquipment.patchValue({
      assetId: '',
    });
  }

  onSubmit() {
    this.store.dispatch(modalShow({
      title: '¿Estás seguro de actualizar esta infracción?',
      text: `
        Si la información está correcta, continúa en Actualizar.
      `,
      text2: 'De lo contrario, presiona  Volver para revisar la información.',
      acceptButtonText: 'Actualizar',
      actionOnAccept: () => {
        this.store.dispatch(loaderAnimationShow());
        const formValues = this.form.value;

        const dateTimeInfringement = <Date>formValues.detail.day;
        if (formValues.detail.hour) {
          const [hour, min] = formValues.detail.hour.split(':');
          dateTimeInfringement.setMinutes(+min);
          dateTimeInfringement.setHours(hour - (dateTimeInfringement.getTimezoneOffset() / 60));
        }

        const selectedPersonId = formValues.person.id;
        const responsableId = formValues.detail.personResponsibleId;
        const reporterId = formValues.detail.reporterPersonId;
        let witnessesIds: number[] = formValues.detail.witnesses; // vector
        witnessesIds = witnessesIds.filter((witness) => witness > 0);
        const observables = [];
        if (selectedPersonId > 0) {
          observables.push(this.personService.getPersonDataById(selectedPersonId));
        }
        else {
          observables.push(of({ mdmid: undefined }));
        }
        if (responsableId > 0) {
          observables.push(this.personService.getPersonDataById(responsableId));
        }
        else {
          observables.push(of({ mdmid: undefined }));
        }
        if (reporterId > 0) {
          observables.push(this.personService.getPersonDataById(reporterId));
        }
        else {
          observables.push(of({ mdmid: undefined }));
        }
        if (witnessesIds.length > 0) {
          witnessesIds.map((id) => observables.push(this.personService.getPersonDataById(id)));
        }

        const persons$ = combineLatest(observables);

        persons$.subscribe({
          next: ([selectedPerson, responsable, reporter, ...witnesses]) => {
            const outt: Record<string, any> = {
              assetId: +formValues.locationAndEquipment.assetId,
              zoneId: +formValues.locationAndEquipment.zoneId,
              equipmentId: formValues.locationAndEquipment.vehicleTypeId,
              equipmentPatent: formValues.locationAndEquipment.patent,
              dateTimeInfringement,
              responsableMdmid: responsable.mdmid,
              severityId: formValues.detail.severityTypeId,
              infringementTypeId: formValues.detail.infrigmentTypeId,
              observation: formValues.detail.observation,
              infringementReportId: formValues.detail.reporterTypeId,
              infringementReportMdmid: reporter.mdmid,
              sanctionTypeId: +formValues.sanctionType.sanctionTypeId,
              suspensionDaysId: formValues.sanctionType.suspensionDays,
              dateStart: formValues.sanctionType.sanctionRange.start || formValues.sanctionType.expirationDate,
              dateEnd: formValues.sanctionType.sanctionRange.end,
              status: 1,
              driverMdmid: selectedPerson.mdmid,
              infringementEvidence: (formValues.detail.evidenceTypes as number[]).map((id) => ({ evidenceId: id })),
              infringementWitness: witnesses.map((witness) => ({
                witnessMdmid: witness.mdmid,
              })),
            };

            const cleanedOut = Object.keys(outt).filter((key) => {
              const value = outt[key];
              if (value) {
                if (!Number.isNaN(+value)) {
                  if (value >= 1) {
                    return true;
                  }
                }
                else {
                  return true;
                }
              }
              return false;
            }).reduce<Record<string, any>>((obj, key) => {
              // eslint-disable-next-line no-param-reassign
              obj[key] = outt[key];
              return obj;
            }, {});

            if (this.selectedInfringement) {
              const currentOfuscatedData = this.router.url.split('/').reverse()[1];

              this.infractionService.updateInfringement(this.selectedInfringement, cleanedOut).subscribe({
                next: ({ id }) => {

                  this.store.dispatch(loaderAnimationHide());

                  const ofuscatedData = ofuscate({
                    title: 'Modificar infracción',
                    subtitle: 'La infracción registrada ha sido actualizada correctamente',
                    messageBold: `N° registro: ${id}`,
                    message: `Una copia de la modificación fue enviada al
                correo electrónico ${this.form.value.person.email}`,
                    actionButtonPath: `/search/infraction/result/${currentOfuscatedData}/infractions`,
                    actionButtonText: 'Ir al perfil de conductor',
                  });

                  this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);
                },
                error: () => {
                  this.store.dispatch(loaderAnimationHide());

                  this.store.select('pageError').subscribe({
                    next: (pageConfig) => {
                      const [website, ...url] = window.location.href.split('//')[1].split('/');
                      if (pageConfig.errorType !== 'Mulesoft') {

                        const ofuscatedData = ofuscate({
                          variant: 'failure',
                          title: 'Modificar infracción',
                          subtitle: 'Hubo un problema y no hemos podido actualizar la infracción. Por favor intenta de nuevo.',
                          actionButtonPath: `/search/infraction/result/${currentOfuscatedData}/infractions`,
                          actionButtonText: 'Ir al perfil de conductor',
                        });

                        this.router.navigateByUrl(`/search/action/result/${ofuscatedData}`);

                      }
                    },
                  });

                },
              });
            }
            else {
              this.infractionService.createInfringement(cleanedOut).subscribe({
                next: ({ id }) => {
                  const ofuscatedData = ofuscate({
                    email: this.form.value.person.email, infractionId: id,
                  });
                  this.store.dispatch(loaderAnimationHide());
                  this.router.navigateByUrl(`/infractions/enter/success/${ofuscatedData}`);
                },
                error: () => {
                  this.store.dispatch(loaderAnimationHide());

                  this.store.select('pageError').subscribe({
                    next: (pageConfig) => {
                      const [website, ...url] = window.location.href.split('//')[1].split('/');
                      if (pageConfig.errorType !== 'Mulesoft') {
                        this.router.navigateByUrl('/infractions/enter/failure');

                      }
                    },
                  });

                },
              });
            }

          },
          error: () => {
            this.store.dispatch(loaderAnimationHide());

            this.store.select('pageError').subscribe({
              next: (pageConfig) => {
                const [website, ...url] = window.location.href.split('//')[1].split('/');
                if (pageConfig.errorType !== 'Mulesoft') {
                  this.router.navigateByUrl('/infractions/enter/failure');

                }
              },
            });
          },
        });
      },
    }));
  }

  goToCreate() {
    const ofuscatedData = ofuscate({ personId: this.selectedPersonData.id });
    this.router.navigateByUrl(`/infractions/enter/${ofuscatedData}`);
  }

}
